<template>
    <ui-container class="space-y-8 py-8">

        <!-- Header. -->
        <ui-header :title="item.name" :subtitle="[`#${item.reference}`, item.inUseBy ? item.inUseBy : 'In Warehouse']"
                   :image="item.imageUrl">
            <template #actions>
                <ui-button-new icon="fas fa-pencil" @click="edit()">Edit</ui-button-new>
                <ui-button-new icon="fas fa-trash" danger @click="openDeleteModal">Delete</ui-button-new>
            </template>
        </ui-header>

        <ui-tabs active="overview" :tabs="tabs" #default="{ isActive }">
            <!-- Overview. -->
            <div v-if="isActive('overview')">
                <ui-data-grid>
                    <ui-data-grid-item label="Reference" :value="item.reference"></ui-data-grid-item>
                    <ui-data-grid-item label="QR URL" :value="item.qrLookup"></ui-data-grid-item>
                    <ui-data-grid-item label="SKU" :value="item.sku"></ui-data-grid-item>
                    <ui-data-grid-item label="Weight" :value="item.weight"></ui-data-grid-item>
                    <ui-data-grid-item label="Created By" :value="item.createdBy"></ui-data-grid-item>
                    <ui-data-grid-item label="Created At" :value="item.createdOn"></ui-data-grid-item>
                    <ui-data-grid-item wide label="Description" :value="item.description"></ui-data-grid-item>
                    <ui-data-grid-item wide label="Locations">
                        <div v-if="item.locations.length"
                             class="flex flex-col rounded-xl border border-gray-200 divide-y divide-gray-200">
                            <div v-for="location in item.locations">
                                <ui-mini-crud-card :title="location.dateTime">
                                    <p class="mt-2 text-gray-500 text-sm">
                                        <a v-if="location"
                                           :href="'https://www.google.com/maps/?z=12&t=m&q=loc:'+location.latitude+'+'+location.longitude"
                                           target="_blank">
                                            {{ location.latitude_dms }} • {{ location.longitude_dms }}
                                        </a>
                                    </p>
                                </ui-mini-crud-card>
                            </div>
                        </div>
                    </ui-data-grid-item>
                    <ui-data-grid-item wide label="Equipment Usages For Packages">
                        <assets-equipment-usage-show :equipment="item"></assets-equipment-usage-show>
                    </ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Purchase Details. -->
            <div v-if="isActive('purchase_details')">
                <ui-data-grid>
                    <ui-data-grid-item label="Supplier" :value="item.supplier"></ui-data-grid-item>
                    <ui-data-grid-item label="Purchase Date" :value="item.purchaseDate"></ui-data-grid-item>
                    <ui-data-grid-item label="Purchase Price" :value="item.purchasePrice"></ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Usages Log. -->
            <div v-if="isActive('usages_log')">
                <ui-data-grid>
                    <ui-data-grid-item wide label="Log New Usage">
                        <assets-equipment-usage-form :equipment="item"></assets-equipment-usage-form>
                    </ui-data-grid-item>
                    <ui-data-grid-item wide label="Equipment Usages For Packages"
                                       :key="`${item.id}-equipmentusage-${(item.usageLogs || []).length}-${item.inUse}`">
                        <assets-equipment-usage-show :equipment="item"></assets-equipment-usage-show>
                    </ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Inspections. -->
            <div v-if="isActive('inspections')">
                <ui-data-grid>
                    <ui-data-grid-item wide label="Log New Inspection">
                        <assets-equipment-inspection-form :equipment="item"></assets-equipment-inspection-form>
                    </ui-data-grid-item>
                    <ui-data-grid-item wide label="Previous Inspections"
                                       :key="`${item.id}-logs-${(item.inspections || []).length}`">
                        <ui-mini-crud :fetch="() => (item.inspections.reverse() || [])"
                                      empty-state-text="This equipment has no inspections. Use the form above to create a new log entry.">
                            <template #show="{ item }">
                                <div class="p-4 flex flex-col xl:flex-row space-y-4 xl:space-y-0 xl:space-x-4">
                                    <div class="flex-1 flex flex-col">
                                        <div class="flex flex-row">
                                            <div class="flex-1">
                                                <h2 class="flex-1 text-sm font-medium text-gray-900">
                                                    {{ item.formattedDate }}
                                                    <span v-if="item.carriedOutBy"
                                                          v-text="' • '+ item.carriedOutBy"></span>
                                                </h2>
                                                <p class="mt-1 text-sm text-gray-500" v-if="item.type"
                                                   v-text="item.type"></p>
                                                <p class="mt-1 text-sm text-gray-500" v-if="item.notes"
                                                   v-text="item.notes"></p>
                                            </div>
                                            <div class="flex-shrink-0 flex space-x-4 items-start" v-if="item.signature">
                                                <img :src="item.signature" style="max-width:150px; max-height: 75px;">
                                            </div>
                                        </div>
                                        <div class="flex-grow flex" v-if="item.attachments.length">
                                            <label class="pl-0 pr-0 py-0.5 pt-1 mx-0 my-1 text-gray-500 text-xs">Attachments:</label>
                                            <div v-for="attachment in item.attachments"
                                                 class="border rounded border-gray-100 px-1.5 py-0.5 mx-1 my-1 bg-gray-50 text-gray-400 text-xs hover:bg-blue-50 flex-shrink cursor-pointer"
                                                 v-text="attachment.name"
                                                 @click="go(attachment.url)"
                                            >
                                            </div>
                                        </div>
                                    </div>
                                    <ui-dropdown menu-class="p-3 w-48">
                                        <template #default="{ toggle }">
                                            <button @click="toggle"
                                                    class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative p-1 rounded">
                                                <i class="fa fa-ellipsis-h"></i>
                                            </button>
                                        </template>

                                        <template #menu="{ close }">
                                            <ui-dropdown-item icon="fas fa-clipboard-list"
                                                              @click="deleteInspection(item);close();">Delete
                                            </ui-dropdown-item>
                                        </template>
                                    </ui-dropdown>
                                </div>
                            </template>
                        </ui-mini-crud>
                    </ui-data-grid-item>
                </ui-data-grid>
            </div>

            <!-- Files. -->
            <div v-else-if="isActive('files')">
                <fs filesystem-title="" path="" :filesystem-id="item.filesystemId"></fs>
            </div>
        </ui-tabs>

    </ui-container>
</template>

<script>
import Fs from "../filesystem/Fs.vue";

export default {
    components: {Fs},
    props: {
        item: Object,
        edit: Function,
        removeItem: Function,
    },
    computed: {
        tabs() {
            return [
                {value: 'overview', label: 'Overview'},
                {value: 'purchase_details', label: 'Purchase Details'},
                {value: 'usages_log', label: 'Usages Log'},
                {value: 'inspections', label: 'Inspections'},
                {value: 'files', label: 'Files'},
            ]
        },
    },
    methods: {
        go(url) {
            window.location = url;
        },
        deleteInspection(inspection) {
            const title = 'Are you sure you want to delete inspection "' + inspection.date + ' by ' + inspection.carriedOutBy + '" ?'
            const description = 'Deleting this equipment inspection is irreversible. Are you sure you want to delete it?'
            eventBus.$emit('delete-modal-open', title, description, async () => {
                await this.item.deleteInspection(inspection)

            });
        },
        openDeleteModal() {
            const title = 'Are you sure you want to delete "' + this.item.name + '"?'
            const description = 'Deleting this equipment is irreversible. Are you sure you want to delete it?'
            eventBus.$emit('delete-modal-open', title, description, async () => {
                await this.item.delete()
                this.removeItem(this.item)

            });
        },
    },
}
</script>
